// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — Cover Page (A4 first page)
// ═══════════════════════════════════════════════════════════════

import { FONTS, DOC_COLORS, TABLE_STYLES } from '../../../../../config/documentStyles';
import { DOC_TYPES, formatDate } from '../../../../../lib/design';
import { StatusBadge } from '../../../../../lib/ui';
import { useI18n } from '../../../../../i18n';
import type { Documento, DocumentVersion, EstadoDocumento } from '../../../../../types';

export interface CoverPageProps {
  doc: Documento;
  estado: EstadoDocumento;
  latestVersion: DocumentVersion | null;
  a4Style: React.CSSProperties;
  renderPageFooter: (pageNum: number, sectionTitle: string) => React.ReactNode;
  totalPages: number;
}

export function CoverPage({
  doc,
  estado,
  latestVersion,
  a4Style,
  renderPageFooter,
}: CoverPageProps) {
  const { t } = useI18n();
  return (
    <div style={a4Style}>
      <div style={{ padding: `60px 64px`, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <span
            style={{
              fontFamily: FONTS.normative,
              fontSize: 10,
              fontWeight: 700,
              background: DOC_COLORS.h1,
              color: 'white',
              padding: '4px 14px',
              borderRadius: 4,
              letterSpacing: 1,
              textTransform: 'uppercase',
            }}
          >
            {(t.docTypes as Record<string, string>)[doc.tipoDocumental] ??
              DOC_TYPES[doc.tipoDocumental]?.label ??
              doc.tipoDocumental}
          </span>
          <StatusBadge status={estado} />
        </div>

        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          <img
            src="/img/Logo-horizontal-Color.png"
            alt=""
            style={{ height: 60, width: 'auto', marginBottom: 48 }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <div
            style={{
              fontFamily: 'monospace',
              fontSize: 16,
              color: DOC_COLORS.h2,
              letterSpacing: 2,
              marginBottom: 12,
            }}
          >
            {doc.codigo}
          </div>
          <h1
            style={{
              fontFamily: FONTS.normative,
              fontSize: '22pt',
              fontWeight: 700,
              color: DOC_COLORS.h1,
              margin: '0 0 16px',
              lineHeight: 1.3,
              maxWidth: 500,
            }}
          >
            {doc.nombre}
          </h1>
          <div style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
            Ed. {String(doc.edicionActual).padStart(2, '0')}
          </div>
          {doc.proceso && (
            <div style={{ fontSize: 13, color: '#555' }}>
              {doc.proceso.codigo} — {doc.proceso.nombre}
            </div>
          )}
          {doc.empresa && (
            <div style={{ fontSize: 12, color: '#8B92A5', marginTop: 8 }}>{doc.empresa}</div>
          )}
        </div>

        {/* Elaborado / Revisado / Aprobado */}
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontFamily: FONTS.normative,
            fontSize: 10,
            marginTop: 24,
          }}
        >
          <thead>
            <tr>
              {[t.viewer.elaboratedBy, t.viewer.reviewedBy, t.viewer.approvedBy].map((h) => (
                <th
                  key={h}
                  style={{
                    background: DOC_COLORS.h1,
                    color: 'white',
                    padding: '6px 12px',
                    fontWeight: 700,
                    border: TABLE_STYLES.tableBorder,
                    width: '33%',
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td
                style={{
                  padding: '8px 12px',
                  border: TABLE_STYLES.tableBorder,
                  color: '#333',
                  textAlign: 'center',
                }}
              >
                {latestVersion?.autor?.nombreCompleto ?? 'Rep. Dirección'}
              </td>
              <td
                style={{
                  padding: '8px 12px',
                  border: TABLE_STYLES.tableBorder,
                  color: '#333',
                  textAlign: 'center',
                }}
              >
                Rep. Dirección
              </td>
              <td
                style={{
                  padding: '8px 12px',
                  border: TABLE_STYLES.tableBorder,
                  color: '#333',
                  textAlign: 'center',
                }}
              >
                {latestVersion?.aprobador?.nombreCompleto ?? 'CEO'}
              </td>
            </tr>
            <tr>
              <td
                style={{
                  padding: '6px 12px',
                  border: TABLE_STYLES.tableBorder,
                  color: '#8B92A5',
                  textAlign: 'center',
                  fontSize: 9,
                }}
              >
                {formatDate(latestVersion?.creadoEn ?? doc.creadoEn)}
              </td>
              <td
                style={{
                  padding: '6px 12px',
                  border: TABLE_STYLES.tableBorder,
                  color: '#8B92A5',
                  textAlign: 'center',
                  fontSize: 9,
                }}
              >
                {formatDate(latestVersion?.fechaAprobacion ?? doc.actualizadoEn)}
              </td>
              <td
                style={{
                  padding: '6px 12px',
                  border: TABLE_STYLES.tableBorder,
                  color: '#8B92A5',
                  textAlign: 'center',
                  fontSize: 9,
                }}
              >
                {formatDate(
                  doc.versionVigente?.fechaAprobacion ??
                    latestVersion?.fechaAprobacion ??
                    doc.actualizadoEn,
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {renderPageFooter(1, doc.codigo + ' — ' + doc.nombre)}
    </div>
  );
}
